import * as Popover from '@radix-ui/react-popover';
import { Braces, Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { JsonTree } from './JsonTree';
import { cn } from '@/lib/cn';

/**
 * A JSON value inside a dense table (e.g. `delay_delivery_tries.payload` — the
 * exact transaction we tried to deliver to the partner).
 *
 * It cannot be inlined: it would blow the row height apart. But it must be fully
 * readable AND copyable — a truncated preview is useless to someone pasting the
 * payload into a ticket. So: a compact trigger that opens the whole thing, with
 * copy always one click away.
 */
export function JsonCell({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);

  const count =
    value && typeof value === 'object'
      ? Array.isArray(value)
        ? value.length
        : Object.keys(value as object).length
      : 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 border border-hairline px-1.5 py-[1px]',
            'font-mono text-[11px] text-text-dim',
            'hover:border-accent/40 hover:text-accent',
            open && 'border-accent/40 text-accent',
          )}
        >
          <Braces size={10} />
          {Array.isArray(value) ? `[${count}]` : `{${count}}`}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="left"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-h-[440px] w-[460px] overflow-auto border border-hairline-strong bg-surface-raised shadow-xl shadow-black/50"
        >
          <div className="sticky top-0 flex items-center justify-between border-b border-hairline bg-surface-raised px-2 py-1.5">
            <span className="eyebrow">json value</span>
            <CopyJson value={value} />
          </div>
          <div className="p-2">
            <JsonTree value={value} defaultOpen={3} />
          </div>
          <Popover.Arrow className="fill-hairline-strong" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Copies the FULL value, pretty-printed — never the truncated preview. */
export function CopyJson({ value, label = 'copy' }: { value: unknown; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard can be blocked in some iframe contexts */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        'inline-flex items-center gap-1 border border-hairline px-1.5 py-[1px]',
        'font-condensed text-[10px] font-semibold uppercase tracking-wider',
        copied ? 'border-ok/40 text-ok' : 'text-text-dim hover:border-accent/40 hover:text-accent',
      )}
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      {copied ? 'copied' : label}
    </button>
  );
}
